import React, { useState, useEffect, useRef, useContext } from "react";
import { useQuery } from "@apollo/client";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Stack,
    Grid,
    Typography,
    Box,
    Collapse,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import UpgradePlan from "../../../../platform/util/upgrade/upgradePlan";
import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../../../platform/graphql/queries";
import { useSelectedBusiness } from "../../../../platform/redux/store";
import { getEffectivePlan } from "../../../../platform/util/utils";

dayjs.extend(customParseFormat);

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ALLOWED_FREQUENCIES = ["Daily", "Weekly", "EveryXHours"];
const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DATE_FORMATS = [
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "M/D/YYYY",
    "MM-DD-YYYY",
    "M-D-YYYY",
    "YYYY/MM/DD",
    "MMM D, YYYY",
    "MMMM D, YYYY",
];
const MAX_OCR_TEXT_CHARS = 12000;

function pickField(obj, keys) {
    for (const key of keys) {
        const value = obj?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return value;
        }
    }
    return "";
}

function parseDateValue(value) {
    if (!value) return null;
    const raw = String(value).trim();

    for (const fmt of DATE_FORMATS) {
        const parsed = dayjs(raw, fmt, true);
        if (parsed.isValid()) return parsed;
    }

    const fallback = dayjs(raw);
    return fallback.isValid() ? fallback : null;
}

function createEmptyMedicationDraft() {
    return {
        name: "",
        dosage: "",
        diagnosis: "",
        sideEffects: "",
        reason: "",
        frequency: "Daily",
        times: [],
        daysOfWeek: [],
        prn: false,
        everyHours: "",
        startHour: null,
        expiryDate: null,
        startDate: dayjs(),
        endDate: null,
        noEndDate: true,
    };
}

function parseMedicationOCRFallback(rawText) {
    const text = String(rawText || "");
    const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    const dosageRegex = /(\d+(?:\.\d+)?\s*(?:mg|mcg|g|ml|units?|iu|tablet(?:s)?|tab(?:s)?|capsule(?:s)?))/i;
    const excludeFieldWords =
        /\b(name|patient|dob|date|address|phone|fax|rx|rx#|quantity|qty|refill|sig|route|doctor|physician|pharmacy|insurance|member|id|diagnosis|prescriber)\b/i;
    const likelyMedicationPattern =
        /\b([A-Z][a-z]{2,}(?:\s+[A-Z]?[a-z]{2,}){0,3})\b.*\b(\d+(?:\.\d+)?\s*(?:mg|mcg|g|ml|units?|iu))\b/;
    const seen = new Set();
    const medications = [];

    for (const line of lines) {
        if (line.length < 4 || line.length > 140) continue;
        if (excludeFieldWords.test(line)) continue;

        const dosageMatch = line.match(dosageRegex);
        const likelyMatch = line.match(likelyMedicationPattern);
        const dosage = dosageMatch ? dosageMatch[1] : likelyMatch?.[2] || "";
        if (!dosage) continue;

        let name = line;
        if (dosageMatch) {
            name = line.slice(0, dosageMatch.index).trim();
        }
        name = name
            .replace(/^[-*•\d.)\s]+/, "")
            .replace(/[^a-zA-Z0-9\s-]/g, "")
            .replace(/\s+/g, " ")
            .trim();
        if (name.length > 60) {
            name = name.split(/\s+/).slice(0, 6).join(" ");
        }
        if (/\d/.test(name)) continue;
        if (name.split(/\s+/).length > 6) continue;
        if (!/[a-zA-Z]{3,}/.test(name)) continue;
        if (!name) continue;

        const key = `${name.toLowerCase()}|${dosage.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);

        medications.push({
            name: name || "",
            dosage: dosage || "",
            diagnosis: "",
            sideEffects: "",
            reason: "",
            frequency: "Daily",
            times: [],
            daysOfWeek: [],
            prn: /\bprn\b/i.test(line),
            everyHours: "",
            startHour: "",
            expiryDate: "",
            startDate: "",
            endDate: "",
            noEndDate: true,
        });

        if (medications.length >= 5) break;
    }

    return { medications };
}

function toFriendlyOcrError(err) {
    const rawMessage = String(err?.message || "").trim();
    const status = Number(err?.status || err?.response?.status || 0);
    const lower = rawMessage.toLowerCase();

    if (lower.includes("connection error") || lower.includes("network") || lower.includes("failed to fetch")) {
        return "Could not reach AI parsing service. Check internet/VPN and try again.";
    }
    if (status === 401 || lower.includes("api key")) {
        return "Server AI parsing is not configured. Using local OCR fallback.";
    }
    if (status === 429 || lower.includes("rate limit")) {
        return "AI service is busy right now. Please wait a moment and try again.";
    }
    if (status >= 500) {
        return "AI parsing service is temporarily unavailable. Please try again.";
    }
    if (rawMessage) return rawMessage;
    return "Failed to parse medication file.";
}

function isLikelyNetworkError(err) {
    const rawMessage = String(err?.message || "").toLowerCase();
    return (
        rawMessage.includes("connection error") ||
        rawMessage.includes("network") ||
        rawMessage.includes("failed to fetch")
    );
}

async function parseMedicationOCR(rawText) {
    return parseMedicationOCRFallback(String(rawText || "").slice(0, MAX_OCR_TEXT_CHARS));
}

async function extractPdfText(file) {
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    let mergedText = "";

    for (let i = 1; i <= pdf.numPages; i += 1) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str || "").join(" ");

        if (pageText.trim().length > 30) {
            mergedText += `${pageText}\n`;
            continue;
        }

        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;

        await page.render({ canvasContext: ctx, viewport }).promise;
        const {
            data: { text },
        } = await Tesseract.recognize(canvas, "eng");
        mergedText += `${text}\n`;
    }

    return mergedText.trim();
}

async function extractFileText(file) {
    if (file.type === "application/pdf") {
        return extractPdfText(file);
    }

    const {
        data: { text },
    } = await Tesseract.recognize(file, "eng");
    return text;
}

export default function MedicationForm({
    open,
    onClose,
    residentId,
    onSave,
    onSaveMany,
    editMedication,
}) {
    const { providerUser } = useContext(UserContext);
    const selectedBusiness = useSelectedBusiness();
    const businessId = selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId;

    const { data: billingData, loading: billingLoading } = useQuery(GET_BILLING_SUBSCRIPTION, {
        variables: { businessId },
        skip: !businessId,
        fetchPolicy: "network-only",
    });

    const { data: plansData, loading: plansLoading } = useQuery(GET_PLANS, {
        skip: !businessId,
    });

    const subscription = billingData?.getBillingSubscription;
    const premiumPlanId = Number(
        plansData?.plans?.find((plan) => plan.code === "PREMIUM")?.id || 3
    );
    const tokenWithSubscriptionPlan = {
        ...(providerUser || {}),
        planId: subscription?.planId ?? providerUser?.planId,
    };
    const effectivePlanLevel = Number(getEffectivePlan(tokenWithSubscriptionPlan) || 0);
    const hasAiAccess = Boolean(businessId && effectivePlanLevel >= premiumPlanId);
    const checkingPremium = Boolean(businessId) && (billingLoading || plansLoading);

    const [name, setName] = useState("");
    const [dosage, setDosage] = useState("");
    const [diagnosis, setDiagnosis] = useState("");
    const [sideEffects, setSideEffects] = useState("");
    const [reason, setReason] = useState("");
    const [showAdvancedClinical, setShowAdvancedClinical] = useState(false);
    const [frequency, setFrequency] = useState("Daily");
    const [everyHours, setEveryHours] = useState("");
    const [startHour, setStartHour] = useState(null);
    const [expiryDate, setExpiryDate] = useState(null);
    const [times, setTimes] = useState([]);
    const [daysOfWeek, setDaysOfWeek] = useState([]);
    const [prn, setPrn] = useState(false);
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(null);
    const [noEndDate, setNoEndDate] = useState(true);

    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrError, setOcrError] = useState("");
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [savingAll, setSavingAll] = useState(false);
    const [draftMedications, setDraftMedications] = useState([createEmptyMedicationDraft()]);
    const [selectedDraftIndex, setSelectedDraftIndex] = useState(0);
    const networkAiUnavailableRef = useRef(false);
    const fileInputRef = useRef(null);

    const normalizeFrequency = (value) => {
        const raw = String(value || "").toLowerCase().replace(/\s+/g, "");
        if (raw === "weekly") return "Weekly";
        if (raw === "everyxhours" || raw === "everyhours" || raw.includes("every")) return "EveryXHours";
        return "Daily";
    };

    const normalizeTimes = (value) => {
        if (!Array.isArray(value)) return [];
        return value
            .map((t) => {
                const parsed = dayjs(String(t).trim(), ["hh:mm A", "h:mm A", "HH:mm"], true);
                return parsed.isValid() ? parsed.format("hh:mm A") : null;
            })
            .filter(Boolean);
    };

    const normalizeMedication = (medication = {}) => {
        const parsedStartDateValue = pickField(medication, ["startDate", "start_date", "date", "prescriptionDate"]);
        const parsedEndDateValue = pickField(medication, ["endDate", "end_date"]);
        const parsedExpiryDateValue = pickField(medication, ["expiryDate", "expiry_date", "expiresAt", "expiry"]);
        const parsedStartHour = pickField(medication, ["startHour", "start_hour"]);
        const parsedFrequency = pickField(medication, ["frequency", "schedule"]);
        const normalizedStartDate = parseDateValue(parsedStartDateValue);
        const normalizedEndDate = parseDateValue(parsedEndDateValue);
        const normalizedExpiryDate = parseDateValue(parsedExpiryDateValue);
        const normalizedStartHour = parsedStartHour
            ? dayjs(String(parsedStartHour).trim(), ["hh:mm A", "h:mm A", "HH:mm"], true)
            : null;

        return {
            name: pickField(medication, ["name", "medication", "medicationName"]) || "",
            dosage: pickField(medication, ["dosage", "dose", "strength"]) || "",
            diagnosis: pickField(medication, ["diagnosis", "indication"]) || "",
            sideEffects: pickField(medication, ["sideEffects", "side_effects", "adverseEffects"]) || "",
            reason: pickField(medication, ["reason", "notes"]) || "",
            frequency: normalizeFrequency(parsedFrequency),
            times: normalizeTimes(medication.times),
            daysOfWeek: Array.isArray(medication.daysOfWeek)
                ? medication.daysOfWeek.filter((d) => WEEK_DAYS.includes(d))
                : [],
            prn: Boolean(medication.prn),
            everyHours: medication.everyHours ? String(medication.everyHours) : "",
            startHour: normalizedStartHour && normalizedStartHour.isValid() ? normalizedStartHour : null,
            expiryDate: normalizedExpiryDate && normalizedExpiryDate.isValid() ? normalizedExpiryDate : null,
            startDate: normalizedStartDate && normalizedStartDate.isValid() ? normalizedStartDate : dayjs(),
            endDate: normalizedEndDate && normalizedEndDate.isValid() ? normalizedEndDate : null,
            noEndDate: medication.noEndDate !== false && !normalizedEndDate,
        };
    };

    const applyDraftToForm = (medication) => {
        if (!medication) return;
        setName(medication.name || "");
        setDosage(medication.dosage || "");
        setDiagnosis(medication.diagnosis || "");
        setSideEffects(medication.sideEffects || "");
        setReason(medication.reason || "");
        setFrequency(ALLOWED_FREQUENCIES.includes(medication.frequency) ? medication.frequency : "Daily");
        setTimes(Array.isArray(medication.times) ? medication.times : []);
        setDaysOfWeek(Array.isArray(medication.daysOfWeek) ? medication.daysOfWeek : []);
        setPrn(Boolean(medication.prn));
        setEveryHours(medication.everyHours ? String(medication.everyHours) : "");
        setStartHour(medication.startHour || null);
        setExpiryDate(medication.expiryDate || null);
        setStartDate(medication.startDate || dayjs());
        setEndDate(medication.endDate || null);
        setNoEndDate(medication.endDate ? false : medication.noEndDate !== false);
    };

    const updateCurrentDraft = (patch) => {
        setDraftMedications((prev) => {
            const next = [...prev];
            next[selectedDraftIndex] = { ...next[selectedDraftIndex], ...patch };
            return next;
        });
    };

    const buildPayloadFromDraft = (medication) => ({
        residentId,
        name: medication.name || "",
        dosage: medication.dosage || "",
        diagnosis: medication.diagnosis || "",
        sideEffects: medication.sideEffects || "",
        reason: medication.reason || "",
        schedule: {
            frequency: ALLOWED_FREQUENCIES.includes(medication.frequency) ? medication.frequency : "Daily",
            times: Array.isArray(medication.times) ? medication.times : [],
            daysOfWeek: Array.isArray(medication.daysOfWeek) ? medication.daysOfWeek : [],
            prn: Boolean(medication.prn),
        },
        expiryDate: medication.expiryDate ? medication.expiryDate.format("YYYY-MM-DD") : null,
        startDate: (medication.startDate || dayjs()).format("YYYY-MM-DD"),
        endDate: medication.noEndDate ? null : medication.endDate?.format("YYYY-MM-DD"),
    });

    useEffect(() => {
        if (!open) return;

        setOcrError("");
        setCheckPlanNeeded(false);
        setUploadedFileName("");
        setSavingAll(false);
        setShowAdvancedClinical(false);

        if (editMedication) {
            const editDraft = {
                name: editMedication.name || "",
                dosage: editMedication.dosage || "",
                diagnosis: editMedication.diagnosis || "",
                sideEffects: editMedication.sideEffects || "",
                reason: editMedication.reason || "",
                frequency: editMedication.schedule?.frequency || "Daily",
                times: editMedication.schedule?.times || [],
                daysOfWeek: editMedication.schedule?.daysOfWeek || [],
                prn: editMedication.schedule?.prn || false,
                everyHours: "",
                startHour: null,
                expiryDate:
                    editMedication.expiryDate && dayjs(editMedication.expiryDate).isValid()
                        ? dayjs(editMedication.expiryDate)
                        : null,
                startDate: dayjs(editMedication.startDate).isValid() ? dayjs(editMedication.startDate) : dayjs(),
                endDate: editMedication.endDate && dayjs(editMedication.endDate).isValid() ? dayjs(editMedication.endDate) : null,
                noEndDate: !editMedication.endDate,
            };
            setDraftMedications([editDraft]);
            setSelectedDraftIndex(0);
            applyDraftToForm(editDraft);
            if (editDraft.diagnosis || editDraft.sideEffects) {
                setShowAdvancedClinical(true);
            }
            return;
        }

        const blank = createEmptyMedicationDraft();
        setDraftMedications([blank]);
        setSelectedDraftIndex(0);
        applyDraftToForm(blank);
    }, [open, editMedication]);

    const handleFileUpload = async (event) => {
        if (checkingPremium) return;
        if (!hasAiAccess) {
            setCheckPlanNeeded(true);
            return;
        }

        const file = event.target.files?.[0];
        if (!file) return;

        setUploadedFileName(file.name);
        setOcrError("");
        setOcrLoading(true);

        try {
            const rawText = await extractFileText(file);
            if (!rawText?.trim()) {
                throw new Error("Could not read text from the selected file.");
            }

            let parsed;
            if (networkAiUnavailableRef.current) {
                parsed = parseMedicationOCRFallback(rawText);
            } else {
                try {
                    parsed = await parseMedicationOCR(rawText);
                } catch (aiErr) {
                    if (isLikelyNetworkError(aiErr)) {
                        networkAiUnavailableRef.current = true;
                    }
                    parsed = parseMedicationOCRFallback(rawText);
                    if (!Array.isArray(parsed?.medications) || !parsed.medications.length) {
                        throw aiErr;
                    }
                }
            }
            const normalizedList = (Array.isArray(parsed?.medications) ? parsed.medications : [])
                .map((med) => normalizeMedication(med))
                .filter((med) => med.name || med.dosage || med.times.length);

            if (!normalizedList.length) {
                throw new Error("No medication data found from OCR.");
            }

            setDraftMedications(normalizedList);
            setSelectedDraftIndex(0);
            applyDraftToForm(normalizedList[0]);
        } catch (err) {
            setOcrError(toFriendlyOcrError(err));
        } finally {
            setOcrLoading(false);
            if (event?.target) event.target.value = "";
        }
    };

    const handleSelectDraftMedication = (event) => {
        const nextIndex = Number(event.target.value);
        setSelectedDraftIndex(nextIndex);
        applyDraftToForm(draftMedications[nextIndex]);
    };

    const handleAddMedicationEntry = () => {
        const nextDraft = createEmptyMedicationDraft();
        setDraftMedications((prev) => [...prev, nextDraft]);
        const nextIndex = draftMedications.length;
        setSelectedDraftIndex(nextIndex);
        applyDraftToForm(nextDraft);
    };

    const handleRemoveMedicationEntry = () => {
        if (draftMedications.length <= 1) return;
        const nextList = draftMedications.filter((_, idx) => idx !== selectedDraftIndex);
        const nextIndex = Math.max(0, selectedDraftIndex - 1);
        setDraftMedications(nextList);
        setSelectedDraftIndex(nextIndex);
        applyDraftToForm(nextList[nextIndex]);
    };

    const handleSaveAllEntries = async () => {
        if (!onSaveMany || !draftMedications.length) return;

        setSavingAll(true);
        setOcrError("");
        try {
            const payloads = draftMedications.map((med) => buildPayloadFromDraft(med));
            await onSaveMany(payloads);
            onClose();
        } catch (err) {
            setOcrError(err?.message || "Failed to save all prescription entries.");
        } finally {
            setSavingAll(false);
        }
    };

    const generateTimes = () => {
        if (!everyHours || !startHour) return;
        const interval = Number(everyHours);
        if (Number.isNaN(interval) || interval <= 0) return;

        const result = [];
        let current = dayjs(startHour);
        for (let i = 0; i < Math.ceil(24 / interval); i += 1) {
            result.push(current.format("hh:mm A"));
            current = current.add(interval, "hour");
            if (current.diff(startHour, "hour") >= 24) break;
        }

        setTimes(result);
        updateCurrentDraft({ times: result });
    };

    const removeTime = (t) => {
        const nextTimes = times.filter((x) => x !== t);
        setTimes(nextTimes);
        updateCurrentDraft({ times: nextTimes });
    };

    const handleSubmit = () => {
        onSave({
            id: editMedication?.id,
            ...buildPayloadFromDraft({
                name,
                dosage,
                diagnosis,
                sideEffects,
                reason,
                frequency,
                times,
                daysOfWeek,
                prn,
                expiryDate,
                startDate,
                endDate,
                noEndDate,
            }),
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                {editMedication ? "Edit Medication" : "Add Medications"}
            </DialogTitle>

            <DialogContent dividers>
                {checkPlanNeeded && (
                    <UpgradePlan
                        featureName="Medication OCR upload"
                        requiredPlanId={premiumPlanId}
                        onClose={() => setCheckPlanNeeded(false)}
                    />
                )}

                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Multi-Prescription Entry
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.85 }}>
                        Add multiple prescriptions in one save. When you click Add Prescription, the new entry is selected automatically.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.75 }}>
                        Use the dropdown below to switch between prescriptions before saving.
                    </Typography>

                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={handleAddMedicationEntry} disabled={Boolean(editMedication)}>
                            Add Prescription
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={handleRemoveMedicationEntry}
                            disabled={Boolean(editMedication) || draftMedications.length <= 1}
                        >
                            Remove Current Prescription
                        </Button>
                    </Stack>

                    <TextField
                        select
                        fullWidth
                        sx={{ mt: 2 }}
                        label="Currently Editing Prescription"
                        value={selectedDraftIndex}
                        onChange={handleSelectDraftMedication}
                    >
                        {draftMedications.map((med, idx) => (
                            <MenuItem key={`${med.name || "med"}-${idx}`} value={idx}>
                                {`Prescription #${idx + 1}${med.name ? ` - ${med.name}` : ""}`}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        File Parsing (Optional)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                        Select File can parse one or more prescriptions and fill this multi-prescription form.
                    </Typography>

                    <input
                        ref={fileInputRef}
                        hidden
                        type="file"
                        accept="image/*,.pdf,application/pdf"
                        onChange={handleFileUpload}
                    />

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                            if (checkingPremium) return;
                            if (!hasAiAccess) {
                                setCheckPlanNeeded(true);
                                return;
                            }
                            fileInputRef.current?.click();
                        }}
                        disabled={ocrLoading || Boolean(editMedication) || checkingPremium}
                    >
                        {checkingPremium
                            ? "Checking Premium..."
                            : ocrLoading
                                ? "Analyzing File..."
                                : "Select File (Image/PDF)"}
                    </Button>

                    {checkingPremium && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Checking premium subscription...
                        </Typography>
                    )}
                    {!hasAiAccess && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            Premium plan or active trial required to use AI medication file/image parsing.
                        </Typography>
                    )}

                    {uploadedFileName && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Selected: {uploadedFileName}
                        </Typography>
                    )}

                    {ocrError && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            {ocrError}
                        </Typography>
                    )}
                </Box>

                <TextField
                    fullWidth
                    label="Medication Name"
                    sx={{ mt: 2 }}
                    value={name}
                    onChange={(e) => {
                        const next = e.target.value;
                        setName(next);
                        updateCurrentDraft({ name: next });
                    }}
                />

                <TextField
                    fullWidth
                    label="Dosage"
                    sx={{ mt: 2 }}
                    value={dosage}
                    onChange={(e) => {
                        const next = e.target.value;
                        setDosage(next);
                        updateCurrentDraft({ dosage: next });
                    }}
                />

                <TextField
                    fullWidth
                    label="Reason / Indication (Optional)"
                    sx={{ mt: 2 }}
                    value={reason}
                    onChange={(e) => {
                        const next = e.target.value;
                        setReason(next);
                        updateCurrentDraft({ reason: next });
                    }}
                />

                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowAdvancedClinical((prev) => !prev)}
                        sx={{ px: 0 }}
                    >
                        {showAdvancedClinical
                            ? "Hide Advanced Clinical Details"
                            : "Add Advanced Clinical Details (Optional)"}
                    </Button>

                    <Collapse in={showAdvancedClinical}>
                        <TextField
                            fullWidth
                            label="Diagnosis (Optional)"
                            sx={{ mt: 1 }}
                            value={diagnosis}
                            onChange={(e) => {
                                const next = e.target.value;
                                setDiagnosis(next);
                                updateCurrentDraft({ diagnosis: next });
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Side Effects (Optional)"
                            sx={{ mt: 2 }}
                            value={sideEffects}
                            onChange={(e) => {
                                const next = e.target.value;
                                setSideEffects(next);
                                updateCurrentDraft({ sideEffects: next });
                            }}
                        />
                    </Collapse>
                </Box>

                <TextField
                    select
                    fullWidth
                    sx={{ mt: 2 }}
                    label="Frequency"
                    value={frequency}
                    onChange={(e) => {
                        const next = e.target.value;
                        setFrequency(next);
                        updateCurrentDraft({ frequency: next });
                    }}
                >
                    <MenuItem value="Daily">Daily (specific times)</MenuItem>
                    <MenuItem value="Weekly">Weekly (select days)</MenuItem>
                    <MenuItem value="EveryXHours">Every X Hours (auto-generate)</MenuItem>
                </TextField>

                <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                        <Checkbox
                            checked={prn}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setPrn(next);
                                updateCurrentDraft({ prn: next });
                            }}
                        />
                    }
                    label="PRN Medication (As Needed - no times required)"
                />

                {!prn && (
                    <>
                        {frequency === "EveryXHours" && (
                            <Box sx={{ mt: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            type="number"
                                            fullWidth
                                            label="Every X Hours"
                                            value={everyHours}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                setEveryHours(next);
                                                updateCurrentDraft({ everyHours: next });
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <TimePicker
                                            label="Start Time"
                                            value={startHour}
                                            onChange={(v) => {
                                                setStartHour(v);
                                                updateCurrentDraft({ startHour: v || null });
                                            }}
                                            slotProps={{
                                                textField: {
                                                    inputProps: {
                                                        readOnly: true,
                                                    },
                                                },
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Button
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                    fullWidth
                                    onClick={generateTimes}
                                    disabled={!everyHours || !startHour}
                                >
                                    Generate Times
                                </Button>

                                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
                                    {times.map((t) => (
                                        <Button
                                            key={t}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => removeTime(t)}
                                        >
                                            {t} x
                                        </Button>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {frequency !== "EveryXHours" && (
                            <>
                                <Box sx={{ mt: 2, width: 160 }}>
                                    <TimePicker
                                        label="Add Time"
                                        onChange={(v) => {
                                            if (!v) return;
                                            const formatted = dayjs(v).format("hh:mm A");
                                            if (times.includes(formatted)) return;
                                            const nextTimes = [...times, formatted];
                                            setTimes(nextTimes);
                                            updateCurrentDraft({ times: nextTimes });
                                        }}
                                        slotProps={{
                                            textField: {
                                                inputProps: {
                                                    readOnly: true,
                                                },
                                            },
                                        }}
                                    />
                                </Box>

                                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                                    {times.map((t) => (
                                        <Button
                                            key={t}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => removeTime(t)}
                                        >
                                            {t} x
                                        </Button>
                                    ))}
                                </Stack>
                            </>
                        )}

                        {frequency === "Weekly" && (
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                {WEEK_DAYS.map((d) => (
                                    <FormControlLabel
                                        key={d}
                                        control={
                                            <Checkbox
                                                checked={daysOfWeek.includes(d)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        const nextDays = [...daysOfWeek, d];
                                                        setDaysOfWeek(nextDays);
                                                        updateCurrentDraft({ daysOfWeek: nextDays });
                                                    } else {
                                                        const nextDays = daysOfWeek.filter((x) => x !== d);
                                                        setDaysOfWeek(nextDays);
                                                        updateCurrentDraft({ daysOfWeek: nextDays });
                                                    }
                                                }}
                                            />
                                        }
                                        label={d}
                                    />
                                ))}
                            </Stack>
                        )}
                    </>
                )}

                <TextField
                    fullWidth
                    type="date"
                    sx={{ mt: 3 }}
                    label="Medication Expiry Date"
                    InputLabelProps={{ shrink: true }}
                    value={expiryDate ? expiryDate.format("YYYY-MM-DD") : ""}
                    onChange={(e) => {
                        const next = e.target.value ? dayjs(e.target.value) : null;
                        setExpiryDate(next);
                        updateCurrentDraft({ expiryDate: next });
                    }}
                />

                <TextField
                    fullWidth
                    type="date"
                    sx={{ mt: 3 }}
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate.format("YYYY-MM-DD")}
                    onChange={(e) => {
                        const next = dayjs(e.target.value);
                        setStartDate(next);
                        updateCurrentDraft({ startDate: next });
                    }}
                />

                <Stack sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={noEndDate}
                                onChange={(e) => {
                                    const next = e.target.checked;
                                    setNoEndDate(next);
                                    if (next) {
                                        setEndDate(null);
                                        updateCurrentDraft({ noEndDate: true, endDate: null });
                                    } else {
                                        updateCurrentDraft({ noEndDate: false });
                                    }
                                }}
                            />
                        }
                        label="No End Date"
                    />

                    <TextField
                        fullWidth
                        type="date"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        disabled={noEndDate}
                        value={endDate ? endDate.format("YYYY-MM-DD") : ""}
                        onChange={(e) => {
                            const next = e.target.value ? dayjs(e.target.value) : null;
                            setEndDate(next);
                            updateCurrentDraft({ endDate: next, noEndDate: !next });
                        }}
                    />
                </Stack>

                <Button
                    variant="contained"
                    sx={{ mt: 3 }}
                    fullWidth
                    onClick={handleSubmit}
                    disabled={savingAll}
                >
                    Save Medication
                </Button>

                {draftMedications.length > 1 && !editMedication && typeof onSaveMany === "function" && (
                    <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        fullWidth
                        onClick={handleSaveAllEntries}
                        disabled={savingAll || ocrLoading}
                    >
                        {savingAll ? "Saving All..." : `Save All Entries (${draftMedications.length})`}
                    </Button>
                )}
            </DialogContent>
        </Dialog>
    );
}
